"use client";

import { z } from "zod";
import React, { useState } from "react";
import Link from "next/link";
import { Button, Input, Label } from "@/components/ui";
import { cx } from "@/lib/tailwind-utils";
import { useToast } from "@/lib/use-toast";
import { resetPassword } from "@/api/auth/login";
function validateEmail(emailValue: string): string {
  const trimmed = emailValue.trim();
  if (!trimmed) {
    return "Email is required";
  }
  // The API validates with Pydantic's EmailStr, which rejects domains without a
  // dot (e.g. "user@gmail"). Match it here so we never submit a 422.
  if (!z.email().safeParse(trimmed).success) {
    return "Invalid email address. Expected format: user@company.com";
  }
  return "";
}
export interface ResetPasswordFormProps {
  initialEmail?: string;
  tenant?: string;
  onBack: () => void;
  submitButtonText?: string;
  loadingText?: string;
  placeholder?: string;
  submitButtonFullWidth?: boolean;
  className?: string;
  /** When set, shows a "Sign up" link and points users with no account here. */
  signUpHref?: string;
  /** Complete signup on the current surface instead of following a link. */
  onSignUp?: () => void;
}
export function ResetPasswordForm({
  initialEmail = "",
  tenant,
  onBack,
  submitButtonText = "Reset Password",
  loadingText = "Sending reset email...",
  placeholder = "user@company.com",
  submitButtonFullWidth = false,
  className,
  signUpHref,
  onSignUp
}: ResetPasswordFormProps) {
  const [email, setEmail] = useState(initialEmail);
  const [emailError, setEmailError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const {
    toast
  } = useToast();
  const handleEmailBlur = () => {
    setEmailError(validateEmail(email));
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const error = validateEmail(email);
    if (error) {
      setEmailError(error);
      return;
    }
    setEmailError("");
    setIsLoading(true);

    // Enumeration-safe: show the same message whether or not an account exists.
    // The backend 401s when there's no Firebase account for the email, but we
    // deliberately don't reveal that. In portal contexts (signUpHref set) the
    // message also nudges sign-up so users who never registered know where to go.
    const notifyResetRequested = () => {
      toast({
        title: "Check your email",
        description: signUpHref || onSignUp ? "If an account exists for this email, we've sent instructions to reset your password. Haven't signed up yet? Create your account first." : "If an account exists for this email, we've sent you instructions to reset your password.",
        variant: "success"
      });
      onBack();
    };
    try {
      await resetPassword(email, tenant ? {
        tenant
      } : undefined);
      notifyResetRequested();
    } catch (error_0: any) {
      if (error_0?.response?.status === 401) {
        // No account for this email — treat identically to success.
        notifyResetRequested();
        return;
      }
      toast({
        title: "Error",
        description: error_0 instanceof Error ? error_0.message : "Failed to send password reset email",
        variant: "error"
      });
    } finally {
      setIsLoading(false);
    }
  };
  return <div className={cx("space-y-5 sm:space-y-6", className)}>
      <form onSubmit={handleSubmit} noValidate className="flex flex-col space-y-4 sm:space-y-5">
        <div>
          <Label htmlFor="reset-email">Email</Label>
          <Input type="email" id="reset-email" name="email" autoComplete="email" placeholder={placeholder} className="mt-2" value={email} onChange={e_0 => {
          setEmail(e_0.target.value);
          if (emailError) setEmailError("");
        }} onBlur={handleEmailBlur} hasError={!!emailError} errorMessage={emailError || undefined} />
        </div>
        <div className={submitButtonFullWidth ? "" : "flex justify-center"}>
          <Button variant="primary" type="submit" isLoading={isLoading} loadingText={loadingText} className={submitButtonFullWidth ? "w-full" : undefined}>
            {submitButtonText}
          </Button>
        </div>
        <button type="button" onClick={onBack} className={cx("transition-colors duration-200", "text-center text-body-strong text-link", "hover:text-link-hover")}>
          Back to login
        </button>
      </form>
      {(onSignUp || signUpHref) && <p className="text-center text-body text-foreground-secondary">
          Haven&apos;t signed up yet?{" "}
          {onSignUp ? <button type="button" onClick={onSignUp} disabled={isLoading} className="font-semibold text-link hover:text-link-hover hover:underline">
              Sign up
            </button> : signUpHref ? <Link href={signUpHref} className={cx("font-semibold text-link", "hover:text-link-hover underline-offset-2 hover:underline")}>
              Sign up
            </Link> : null}
        </p>}
    </div>;
}